'use no memo';
import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {CUSTOM_CONTROLS_WRAPPER} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import {CustomOutlineBoxes} from '@Components/poster-editor/components/custom-item-controls/components/custom-outline-boxes/custom-outline-boxes';
import {SingleItemCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls';
import {ActiveSelectionCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/active-selection-custom-controls/active-selection-custom-controls';
import {ItemLoadingIndicators} from '@Components/poster-whiteboard/components/item-loading-indicator';
import type {CustomItemControlsWrapperProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import {CANVAS_BACKDROP_CLASS} from '@PosterWhiteboard/page/page.types';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import {useShouldHideControlsWhenItemAnimating, useShouldHideHoveredItemControlsWhenAnimating} from '@Hooks/poster-editor/useShouldHideControlsWhenItemAnimating';
import {useAppSelector} from '@/hooks';

export function CustomItemControlsWrapper(props: CustomItemControlsWrapperProps): ReactElement<any> | null {
  const hideActiveItemControlsDueToAnimation = useShouldHideControlsWhenItemAnimating(props.pageHashedId);
  const hideHoveredItemControlsDueToAnimation = useShouldHideHoveredItemControlsWhenAnimating(props.pageHashedId);
  const hideSelectedOrHoveredItemControlsDueToAnimation = hideActiveItemControlsDueToAnimation || hideHoveredItemControlsDueToAnimation;
  const hideControlsWhileEditing = useAppSelector((state) => {
    const activeItemIds = state.posterEditor.activeItemIds[props.pageHashedId];
    return activeItemIds?.some((itemId) => state.posterEditor.shouldHideControls[itemId]) ?? false;
  });
  const [backdropElement, setBackdropElement] = useState<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const updatePositionRef = useRef<(() => void) | undefined>(undefined);

  const onWrapperMount = (node: HTMLDivElement | null): void => {
    wrapperRef.current = node;
    if (node) {
      updatePositionRef.current?.();
    }
  };

  useEffect(() => {
    const backdropSelector = `.${CANVAS_BACKDROP_CLASS}#${CSS.escape(props.pageHashedId)}`;
    const rootToObserve = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get?.(0) ?? document.body;

    setBackdropElement(document.querySelector<HTMLElement>(backdropSelector));

    const observer = new MutationObserver(() => {
      setBackdropElement(document.querySelector<HTMLElement>(backdropSelector));
    });

    observer.observe(rootToObserve, {childList: true, subtree: true});

    return (): void => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!backdropElement) {
      return;
    }

    const container = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get?.(0);
    if (!container) {
      return;
    }

    const updatePosition = (): void => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }
      const backdropRect = backdropElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      wrapper.style.top = `${backdropRect.top - containerRect.top + container.scrollTop}px`;
      wrapper.style.left = `${backdropRect.left - containerRect.left + container.scrollLeft}px`;
    };

    updatePositionRef.current = updatePosition;
    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    window.addEventListener('resize', updatePosition);
    container.addEventListener('scroll', updatePosition);
    resizeObserver.observe(backdropElement);
    resizeObserver.observe(container);

    return (): void => {
      updatePositionRef.current = undefined;
      window.removeEventListener('resize', updatePosition);
      container.removeEventListener('scroll', updatePosition);
      resizeObserver.disconnect();
    };
  }, [backdropElement]);

  if (!backdropElement || hideSelectedOrHoveredItemControlsDueToAnimation) {
    return null;
  }

  const page = window.posterEditor?.whiteboard?.pages?.getPage(props.pageHashedId);
  if (!page) {
    return null;
  }

  const container = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get?.(0);
  if (!container) {
    return null;
  }

  const getSelectionControls = (): ReactElement | null => {
    if (hideControlsWhileEditing) {
      return null;
    }
    return (
      <>
        <CustomOutlineBoxes pageHashedId={props.pageHashedId} />
        <SingleItemCustomControls pageHashedId={props.pageHashedId} />
        <ActiveSelectionCustomControls pageHashedId={props.pageHashedId} />
      </>
    );
  };

  return createPortal(
    <div ref={onWrapperMount} style={{position: 'absolute', top: 0, left: 0, zIndex: 1, contain: 'layout'}} className={CUSTOM_CONTROLS_WRAPPER} id={props.pageHashedId}>
      {getSelectionControls()}
      <ItemLoadingIndicators pageHashedId={props.pageHashedId} />
    </div>,
    container
  );
}
